import type { GroupComparisonsMetricFocus, GroupComparisonsSchoolRow } from '@/features/reports/api/groupComparisons.dto';
import {
  focusMetricValue,
  focusVariance,
  isOutlierOnFocus,
  metricColumnHeader,
} from '@/features/reports/data/groupComparisonsMock';
import { downloadStringMatrixAsPdf, downloadStringMatrixAsXlsx } from '@/features/reports/utils/teacherEquityAlertExports';

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function fmtPct(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function buildHeaders(metricFocus: GroupComparisonsMetricFocus): string[] {
  return [
    'School',
    'Governorate',
    'Code',
    'Students',
    metricColumnHeader(metricFocus),
    'vs peers %',
    'Outlier (focused metric)',
  ];
}

export function buildGroupComparisonsExportMatrix(
  rows: GroupComparisonsSchoolRow[],
  metricFocus: GroupComparisonsMetricFocus,
  outlierThreshold: number
): string[][] {
  return rows.map((r) => [
    r.schoolName,
    r.governorateName,
    r.schoolCode,
    String(r.studentsCount),
    fmtUsd(focusMetricValue(r, metricFocus)),
    fmtPct(focusVariance(r, metricFocus)),
    isOutlierOnFocus(r, metricFocus, outlierThreshold) ? 'Yes' : 'No',
  ]);
}

export async function exportGroupComparisonsExcel(
  rows: GroupComparisonsSchoolRow[],
  metricFocus: GroupComparisonsMetricFocus,
  basename: string,
  outlierThreshold: number
): Promise<void> {
  const headers = buildHeaders(metricFocus);
  const matrix = buildGroupComparisonsExportMatrix(rows, metricFocus, outlierThreshold);
  await downloadStringMatrixAsXlsx(basename, 'Group comparisons', headers, matrix);
}

export async function exportGroupComparisonsPdf(
  rows: GroupComparisonsSchoolRow[],
  metricFocus: GroupComparisonsMetricFocus,
  basename: string,
  subtitles: string[],
  outlierThreshold: number
): Promise<void> {
  const headers = buildHeaders(metricFocus);
  const matrix = buildGroupComparisonsExportMatrix(rows, metricFocus, outlierThreshold);
  await downloadStringMatrixAsPdf(basename, subtitles, headers, matrix);
}

export function exportBasename(year: string, metricFocus: GroupComparisonsMetricFocus): string {
  return `group-comparisons_${year}_${metricFocus}`;
}
